import React, { useMemo, useState } from 'react';
import {
  Container,
  Errors,
  IncludeExcludeButton,
  IncludeExcludeButtonsContainer,
  ItemButton,
  ItemsContainer,
  ListContainer,
  ListTitle,
} from './styles';

interface Item {
  id: number;
  name: string;
  included: boolean;
  canExclude: boolean;
}

interface IncludeExcludeMenuProps {
  includedTitle: string;
  excludedTitle: string;
  items: Item[];
  onChange: (items: Item[]) => void;
  renderErrorMessage: (itemsWithError: Item[]) => string;
}

export const IncludeExcludeMenu: React.FC<IncludeExcludeMenuProps> = ({
  items,
  includedTitle,
  excludedTitle,
  onChange,
  renderErrorMessage,
}) => {
  const [itemsToInclude, setItemsToInclude] = useState<Item[]>([]);
  const [itemsToExclude, setItemsToExclude] = useState<Item[]>([]);
  const [itemsWithError, setItemsWithError] = useState<Item[]>([]);

  const includedItems = useMemo(() => items.filter(({ included }) => included), [items]);
  const excludedItems = useMemo(() => items.filter(({ included }) => !included), [items]);

  return (
    <>
      <Container>
        <ListContainer>
          <ListTitle>{includedTitle}</ListTitle>
          <ItemsContainer>
            {includedItems.map(item => {
              const isSelectedToExclude = itemsToExclude.some(({ id }) => item.id === id);

              return (
                <ItemButton
                  isSelected={isSelectedToExclude}
                  onClick={() => {
                    setItemsToInclude([]);

                    setItemsToExclude(
                      isSelectedToExclude
                        ? itemsToExclude.filter(({ id }) => item.id !== id)
                        : [...itemsToExclude, item],
                    );
                  }}
                  key={item.id}
                >
                  {item.name}
                </ItemButton>
              );
            })}
          </ItemsContainer>
        </ListContainer>

        <IncludeExcludeButtonsContainer>
          <IncludeExcludeButton
            appendIcon="fas fa-arrow-alt-right"
            aria-label="Transfer included to excluded"
            onClick={() => {
              setItemsWithError(itemsToExclude.filter(({ canExclude }) => !canExclude));

              const idsToExclude = itemsToExclude.map(({ id }) => id);

              onChange(
                items.map(item =>
                  item.canExclude && idsToExclude.indexOf(item.id) >= 0
                    ? { ...item, included: false }
                    : item,
                ),
              );

              setItemsToExclude([]);
            }}
          />
          <IncludeExcludeButton
            appendIcon="fas fa-arrow-alt-left"
            aria-label="Transfer excluded to included"
            onClick={() => {
              setItemsWithError([]);

              const idsToInclude = itemsToInclude.map(({ id }) => id);

              onChange(
                items.map(item =>
                  idsToInclude.indexOf(item.id) >= 0 ? { ...item, included: true } : item,
                ),
              );

              setItemsToExclude([]);
            }}
          />
        </IncludeExcludeButtonsContainer>

        <ListContainer>
          <ListTitle>{excludedTitle}</ListTitle>
          <ItemsContainer>
            {excludedItems.map(item => {
              const isSelectedToInclude = itemsToInclude.some(({ id }) => item.id === id);

              return (
                <ItemButton
                  isSelected={isSelectedToInclude}
                  onClick={() => {
                    setItemsToExclude([]);

                    setItemsToInclude(
                      isSelectedToInclude
                        ? itemsToInclude.filter(({ id }) => item.id !== id)
                        : [...itemsToInclude, item],
                    );
                  }}
                  key={item.id}
                >
                  {item.name}
                </ItemButton>
              );
            })}
          </ItemsContainer>
        </ListContainer>
      </Container>
      {itemsWithError.length > 0 && <Errors>{renderErrorMessage(itemsWithError)}</Errors>}
    </>
  );
};
